import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Server-authoritative entitlement summary — DO NOT compute client-side.
 * Reads `/api/entitlement/summary`, which returns:
 *   { gate_enabled, initial_allowance, per_paid_order,
 *     qualifying_paid_checkouts, unique_free_claims_used,
 *     entitlement_total, remaining, next_unlock_amount }
 *
 * When logged out, resolves to `null` (no fetch, no user data).
 * Consumers should treat `remaining` as authoritative for display —
 * never derive it from local purchase history.
 */
export function useEntitlement() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const reload = useCallback(async () => {
    if (!user) { setSummary(null); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/entitlement/summary");
      setSummary(data);
    } catch (e) {
      setError(e?.response?.status || "network");
      setSummary(null);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  return { summary, loading, error, reload };
}
